import { Controller } from "@hotwired/stimulus"

// Controller for event gallery media upload with preview and friend tagging
export default class extends Controller {
  static targets = [
    "fileInput", 
    "placeholder", 
    "preview", 
    "image", 
    "video",
    "friendSearch",
    "friendResults", 
    "taggedFriends",
    "taggedIds"
  ]
  
  static values = {
    eventId: String
  }

  connect() {
    this.currentFile = null
    this.taggedUserIds = []
    this.taggedFriendsData = {}
    this.friends = []
    this.searchTimeout = null

    // Close results when clicking outside
    document.addEventListener("click", this.handleOutsideClick)
  }

  disconnect() {
    document.removeEventListener("click", this.handleOutsideClick)
  }

  handleOutsideClick = (event) => {
    if (!this.element.contains(event.target)) {
      this.hideFriendResults()
    }
  }

  //==========================================================================
  // File Preview
  //==========================================================================

  previewFile(event) {
    const file = event.target.files[0]
    if (!file) {
      this.resetPreview()
      return
    }

    this.currentFile = file
    const objectUrl = URL.createObjectURL(file)
    const isVideo = file.type.startsWith("video/")

    // Hide placeholder, show preview
    if (this.hasPlaceholderTarget) {
      this.placeholderTarget.classList.add("hidden")
    }
    if (this.hasPreviewTarget) {
      this.previewTarget.classList.remove("hidden")
    }

    // Display media
    if (isVideo) {
      if (this.hasVideoTarget) {
        this.videoTarget.src = objectUrl
        this.videoTarget.classList.remove("hidden")
      }
      if (this.hasImageTarget) {
        this.imageTarget.classList.add("hidden")
      }
    } else {
      if (this.hasImageTarget) {
        this.imageTarget.src = objectUrl
        this.imageTarget.classList.remove("hidden")
      }
      if (this.hasVideoTarget) {
        this.videoTarget.classList.add("hidden")
      }
    }
  }

  resetPreview() {
    this.currentFile = null
    
    if (this.hasPlaceholderTarget) {
      this.placeholderTarget.classList.remove("hidden")
    }
    if (this.hasPreviewTarget) {
      this.previewTarget.classList.add("hidden")
    }
    if (this.hasImageTarget) {
      this.imageTarget.src = ""
    }
    if (this.hasVideoTarget) {
      this.videoTarget.src = ""
    }
  }

  //==========================================================================
  // Friend Tagging
  //==========================================================================

  searchFriends(event) {
    const query = event.target.value.trim()
    
    // Clear previous timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    if (query.length < 2) {
      this.hideFriendResults()
      return
    }

    // Debounce search
    this.searchTimeout = setTimeout(() => {
      this.fetchFriends(query)
    }, 300)
  }

  async fetchFriends(query) {
    try {
      const response = await fetch(`/friends.json?query=${encodeURIComponent(query)}&limit=10`)
      if (!response.ok) throw new Error("Failed to fetch friends")
      
      const friends = await response.json()
      this.friends = friends
      this.renderFriendResults(friends)
    } catch (error) {
      console.error("Error fetching friends:", error)
      this.hideFriendResults()
    }
  }

  renderFriendResults(friends) {
    if (!this.hasFriendResultsTarget) return
    
    // Filter out already tagged friends
    const availableFriends = friends.filter(f => !this.taggedUserIds.includes(f.id))
    
    if (availableFriends.length === 0) {
      this.hideFriendResults()
      return
    }

    this.friendResultsTarget.innerHTML = availableFriends.map(friend => `
      <button type="button"
              class="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              data-action="click->event-media-form#tagFriend"
              data-friend-id="${friend.id}"
              data-friend-name="${friend.display_name}"
              data-friend-avatar="${friend.avatar_url || ''}">
        ${friend.avatar_url 
          ? `<img src="${friend.avatar_url}" class="w-8 h-8 rounded-full object-cover" alt="">`
          : `<div class="w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-sm font-medium">${friend.display_name.charAt(0)}</div>`
        }
        <span class="text-sm text-gray-900 dark:text-white">${friend.display_name}</span>
      </button>
    `).join("")

    this.friendResultsTarget.classList.remove("hidden")
  }

  hideFriendResults() {
    if (this.hasFriendResultsTarget) {
      this.friendResultsTarget.classList.add("hidden")
      this.friendResultsTarget.innerHTML = ""
    }
  }

  tagFriend(event) {
    const friendId = event.currentTarget.dataset.friendId
    const friendName = event.currentTarget.dataset.friendName
    const friendAvatar = event.currentTarget.dataset.friendAvatar

    if (this.taggedUserIds.includes(friendId)) return

    // Store friend data for rendering
    if (!this.taggedFriendsData) {
      this.taggedFriendsData = {}
    }
    this.taggedFriendsData[friendId] = {
      id: friendId,
      display_name: friendName,
      avatar_url: friendAvatar
    }

    this.taggedUserIds.push(friendId)
    this.renderTaggedFriends()
    this.updateTaggedIdsInput()
    this.hideFriendResults()
    
    if (this.hasFriendSearchTarget) {
      this.friendSearchTarget.value = ""
    }
  }

  removeTag(event) {
    const friendId = event.currentTarget.dataset.friendId
    this.taggedUserIds = this.taggedUserIds.filter(id => id !== friendId)
    this.renderTaggedFriends()
    this.updateTaggedIdsInput()
  }

  renderTaggedFriends() {
    if (!this.hasTaggedFriendsTarget) return

    if (this.taggedUserIds.length === 0) {
      this.taggedFriendsTarget.innerHTML = ""
      return
    }

    // Find friend data for each tagged ID (prioritize stored data, then search results)
    const taggedData = this.taggedUserIds.map(id => {
      // First check stored data from tagFriend
      if (this.taggedFriendsData && this.taggedFriendsData[id]) {
        return this.taggedFriendsData[id]
      }
      // Then check current search results
      const friend = this.friends.find(f => f.id === id)
      return friend || { id, display_name: "Unknown", avatar_url: null }
    })

    this.taggedFriendsTarget.innerHTML = taggedData.map(friend => `
      <div class="inline-flex items-center gap-1 px-2 py-1 bg-sky-100 dark:bg-sky-900/50 text-sky-800 dark:text-sky-300 rounded-full text-sm">
        ${friend.avatar_url 
          ? `<img src="${friend.avatar_url}" class="w-5 h-5 rounded-full object-cover" alt="">`
          : `<div class="w-5 h-5 rounded-full bg-sky-200 dark:bg-sky-800 flex items-center justify-center text-xs">${friend.display_name.charAt(0)}</div>`
        }
        <span>${friend.display_name}</span>
        <button type="button"
                class="ml-1 hover:text-sky-600 dark:hover:text-sky-400"
                data-action="click->event-media-form#removeTag"
                data-friend-id="${friend.id}">
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
    `).join("")
  }

  updateTaggedIdsInput() {
    if (!this.hasTaggedIdsTarget) return
    
    // Clear existing inputs
    this.taggedIdsTarget.innerHTML = ""
    
    // Add hidden inputs for each tagged friend
    this.taggedUserIds.forEach(id => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "media[tagged_user_ids][]"
      input.value = id
      this.taggedIdsTarget.appendChild(input)
    })
  }

  // Close results when clicking outside
  closeResults(event) {
    if (!this.element.contains(event.target)) {
      this.hideFriendResults()
    }
  }
}

